/**
 * API Client - TypeScript HTTP client with authentication
 *
 * Handles:
 * - CSRF token injection
 * - Bearer token authentication
 * - Automatic 401 redirect
 * - Credentials for cookies
 */

import { getLocalStorage } from "../utils";
import { getCookie } from "../utils/cookie";
import { STORAGE_KEYS, ROUTES } from "../constants";

export interface ApiRequestOptions extends Omit<RequestInit, "headers"> {
  headers?: Record<string, string>;
  /** Skip adding Authorization header even if token exists */
  skipAuth?: boolean;
  /** Skip adding CSRF token header */
  skipCsrf?: boolean;
}

export interface ApiResponse<T = unknown> extends Response {
  data?: T;
}

/**
 * Base API request function with authentication headers
 *
 * @param url - The URL to fetch
 * @param options - Request options extending fetch RequestInit
 * @returns Promise<Response>
 *
 * @example
 * // Simple GET request
 * const response = await apiRequest('/api/v1/users/');
 *
 * @example
 * // POST with body
 * const response = await apiRequest('/api/v1/auth/', {
 *   method: 'POST',
 *   body: new URLSearchParams({ email: 'user@example.com' }),
 *   headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
 * });
 */
export async function apiRequest(
  url: string,
  options: ApiRequestOptions = {},
): Promise<Response> {
  const {
    skipAuth = false,
    skipCsrf = false,
    headers = {},
    ...fetchOptions
  } = options;

  const token = getLocalStorage(STORAGE_KEYS.TOKEN);
  const csrfToken = getCookie("csrftoken");

  if (!csrfToken && !skipCsrf) {
    console.warn("[apiRequest] CSRF token not found in cookies for:", url);
  }

  const defaultHeaders: Record<string, string> = {};

  // Add CSRF token if available and not skipped
  if (csrfToken && !skipCsrf) {
    defaultHeaders["X-CSRFToken"] = csrfToken;
  }

  // Add Authorization header if token exists and not skipped
  if (token && !skipAuth) {
    defaultHeaders["Authorization"] = `Bearer ${token}`;
  }

  const mergedOptions: RequestInit = {
    ...fetchOptions,
    credentials: "include", // Required for cookies (CSRF token)
    headers: {
      ...defaultHeaders,
      ...headers,
    },
  };

  const response = await fetch(url, mergedOptions);

  // Handle 401 Unauthorized - clear token and redirect
  if (response.status === 401) {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    if (typeof window !== "undefined") {
      window.location.href = ROUTES.LANDING;
    }
  }

  return response;
}

/**
 * Helper to make JSON API requests
 * Automatically sets Content-Type and parses response
 */
export async function apiRequestJson<T = unknown>(
  url: string,
  options: ApiRequestOptions & { body?: object } = {},
): Promise<{ response: Response; data: T }> {
  const { body, headers = {}, ...rest } = options;

  const response = await apiRequest(url, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await response.json();
  return { response, data };
}

/**
 * Helper to make form-urlencoded API requests
 * Common pattern for Django views
 */
export async function apiRequestForm(
  url: string,
  formData: Record<string, string>,
  options: ApiRequestOptions = {},
): Promise<{ response: Response; data: unknown }> {
  const { headers = {}, ...rest } = options;

  const urlEncodedData = new URLSearchParams();
  Object.entries(formData).forEach(([key, value]) => {
    urlEncodedData.append(key, value);
  });

  const response = await apiRequest(url, {
    method: "POST",
    ...rest,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...headers,
    },
    body: urlEncodedData,
  });

  const data = await response.json();
  return { response, data };
}

export default apiRequest;
